import type { ReactNode } from "react";
import { useInteractiveSplitOverlay } from "@/components/interactive/interactiveSplitOverlayContext";
import { ModalSplitLayout } from "@/components/layout/ModalSplitLayout";
import { screen, useMediaQuery } from "@/hooks/useMediaQuery";
import { cn } from "@/lib/utils";

interface CodeQuizWorkspaceProps {
  /** Left pane, below the review: challenge, intro, revealed solution. */
  meta: ReactNode;
  /**
   * Left pane, after the task: long reference material the learner reads while
   * writing code (e.g. the assertions to satisfy). It shares the pane's scroll
   * with the task and the review.
   */
  reference?: ReactNode;
  /**
   * Left pane, above the task: attempt count and graded review. Shares the
   * pane's scroll, so a long exercise does not trap the review in its own box.
   */
  feedback?: ReactNode;
  /** Right pane: the code editor or answer input. */
  editor: ReactNode;
  /** Action row pinned under the editor (e.g. the submit button). */
  actions?: ReactNode;
  /** Optional progress indicator (e.g. "Aufgabe 1/3"). */
  progress?: ReactNode;
  /**
   * Starting width of the meta (left) pane in the lesson overlay; the learner
   * can drag from there. "editor-wide" (default) gives the editor the larger
   * share — right when the learner writes a lot (CODE_ASSERTION). "even"
   * starts at 1/2–1/2 — right when the left pane holds primary reference
   * content (CODE_EXPLANATION). On the completion-quiz page every question
   * starts centered instead.
   */
  split?: "editor-wide" | "even";
  /**
   * Mobile order. "editor-first" (default) gives the editor a fixed height up
   * top, so a filling CodeMirror has room. "meta-first" lets the right pane
   * follow the task at its natural height — right when it holds answer
   * choices that only make sense after reading the question.
   */
  stackedOrder?: "editor-first" | "meta-first";
}

const META_DEFAULT_SIZE: Record<
  NonNullable<CodeQuizWorkspaceProps["split"]>,
  string
> = {
  "editor-wide": "40%",
  even: "50%",
};

/**
 * The completion quiz draws this workspace on the page, outside the lesson
 * overlay. Every question there opens on the same centered divider, so moving
 * to the next one does not slide the bar.
 */
const PAGE_LAYOUT = {
  id: "code-quiz-workspace-page",
  defaultAsideSize: "50%",
};

const lessonLayout = (split: NonNullable<CodeQuizWorkspaceProps["split"]>) => ({
  id: `code-quiz-workspace-${split}`,
  defaultAsideSize: META_DEFAULT_SIZE[split],
});

/** Neither pane may be dragged narrower than this. */
const MIN_PANE_SIZE = "280px";

type MetaColumnProps = Pick<
  CodeQuizWorkspaceProps,
  "meta" | "reference" | "feedback"
> & {
  /** Mobile: bands flow in the page scroll instead of splitting the height. */
  stacked?: boolean;
};

/**
 * The left column. Review, task, and reference share one scroll on desktop.
 * Stacked, they follow one another in the overlay's own scroll.
 */
const MetaColumn = ({
  meta,
  reference,
  feedback,
  stacked = false,
}: MetaColumnProps) => (
  <div
    data-testid="code-quiz-meta-column"
    className={cn(
      "flex min-w-0 flex-col gap-4",
      !stacked && "min-h-0 flex-1 overflow-y-auto pr-1"
    )}
  >
    {feedback && <div className="min-w-0">{feedback}</div>}

    <div className="min-w-0 space-y-4">{meta}</div>

    {reference && <div className="min-w-0">{reference}</div>}
  </div>
);

/**
 * Two-pane workspace for code quizzes inside the wide overlay: the task on
 * the white left pane, the editor on the tinted right pane, actions pinned
 * beneath both.
 *
 * Desktop (lg+): the divider is draggable and remembered per learner.
 * Mobile: stacked in `stackedOrder`; the overlay body scrolls.
 */
export const CodeQuizWorkspace = ({
  meta,
  reference,
  feedback,
  editor,
  actions,
  progress,
  split = "editor-wide",
  stackedOrder = "editor-first",
}: CodeQuizWorkspaceProps) => {
  const isLargeScreen = useMediaQuery(screen.lg);
  const overlay = useInteractiveSplitOverlay();
  const layout = overlay ? lessonLayout(split) : PAGE_LAYOUT;
  const editorFirst = stackedOrder === "editor-first";

  // Progress sits in the left pane, unless the stacked layout puts the editor
  // on top.
  const headerInAside = isLargeScreen || !editorFirst;
  // The overlay's floating close button covers the top-right corner, which
  // belongs to the aside only when it is stacked on top.
  const asideHoldsCorner = !isLargeScreen && !editorFirst;
  const clearCloseButton = Boolean(overlay);

  const header = progress && (
    <div
      className={cn(
        "flex shrink-0 items-center justify-end gap-4",
        clearCloseButton && asideHoldsCorner && "pr-8"
      )}
    >
      <div className="shrink-0 text-sm font-semibold text-primary/70">
        {progress}
      </div>
    </div>
  );

  const editorTopPadding = () => {
    if (header && !headerInAside) return "pt-4";
    if (clearCloseButton && !asideHoldsCorner) return "pt-14";
    return "pt-6";
  };

  return (
    <ModalSplitLayout
      resizable={{
        id: layout.id,
        defaultAsideSize: layout.defaultAsideSize,
        minPaneSize: MIN_PANE_SIZE,
      }}
      mainFirstWhenStacked={editorFirst}
      asideClassName="gap-4 p-6"
      aside={
        <>
          {headerInAside && header}
          <MetaColumn
            meta={meta}
            reference={reference}
            feedback={feedback}
            stacked={!isLargeScreen}
          />
        </>
      }
      main={
        <>
          {header && !headerInAside && (
            <div className={cn("px-6 pt-6", clearCloseButton && "pr-14")}>
              {header}
            </div>
          )}
          <div
            className={cn(
              "px-6 pb-6",
              editorTopPadding(),
              isLargeScreen ? "min-h-0 flex-1" : editorFirst && "h-[55vh]"
            )}
          >
            <div className="h-full min-h-0">{editor}</div>
          </div>
        </>
      }
      footer={
        actions && (
          <div className="flex shrink-0 items-center justify-end gap-3 border-t border-[#e3e9f2] bg-white px-6 py-3">
            {actions}
          </div>
        )
      }
    />
  );
};
